// src/pages/admin/EditItem.js
import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import { Breadcrumb } from "../../../components";
import { CardHeader } from "../../../components/cards";
import {Box} from "../../../components/elements";
import PageLayout from "../../../layouts/PageLayout";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Button as MuiButton } from '@mui/material';
import axios from "axios";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";

export default function EditItem() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const isMaster = location.pathname.includes("master"); // detect /edit-master-item/:id

  // dropdown data for storehouse mode
  const [storehouses, setStorehouses] = useState([]);
  const [tabs,        setTabs]        = useState([]);
  const [quadrants,   setQuadrants]   = useState([]);
  const [filters,     setFilters]     = useState([]);

  const [item, setItem]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isMaster) {
      // Fetch master-item
      axios
        .get(`${process.env.REACT_APP_BASE_URL}common/master-items/${id}`, { headers })
        .then(({ data }) => {
          setItem({
            ...data,
            ccGL:    data.costCenters?.ccGL    || "",
            ccJob:   data.costCenters?.ccJob   || "",
            ccWty:   data.costCenters?.ccWty   || "",
            ccWo:    data.costCenters?.ccWo    || "",
            ccCapex: data.costCenters?.ccCapex || "",
            glBm:    data.costCenters?.glBm    || "",
            glEm:    data.costCenters?.glEm    || "",
            glStr:   data.costCenters?.glStr   || "",
            indicator: Boolean(data.indicator),
            expires: data.expires
              ? new Date(data.expires).toISOString().slice(0,10)
              : ""
          });
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      // Fetch dropdowns + storehouse-item
      Promise.all([
        axios.get(`${process.env.REACT_APP_BASE_URL}common/storehouses`, { headers }),
        axios.get(`${process.env.REACT_APP_BASE_URL}common/tabs`,        { headers }),
        axios.get(`${process.env.REACT_APP_BASE_URL}common/quadrants`,   { headers }),
        axios.get(`${process.env.REACT_APP_BASE_URL}common/filters`,     { headers }),
        axios.get(`${process.env.REACT_APP_BASE_URL}common/items/${id}`, { headers })
      ])
      .then(([shRes, tabRes, qRes, fRes, itemRes]) => {
        setStorehouses(shRes.data);
        setTabs(tabRes.data);
        setQuadrants(qRes.data);
        setFilters(fRes.data);
        const d = itemRes.data;
        setItem({
          ...d,
          storehouse:  d.storehouse?._id  || "",
          tab:         d.tab?._id        || "",
          quadrant:    d.quadrant?._id   || "",
          filter:      d.filter?._id     || "",
          alwaysCount: Boolean(d.alwaysCount),
          active:      Boolean(d.active)
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    }
  }, [id, isMaster]);

  const saveItem = async () => {
    try {
      if (isMaster) {
        // Build payload for master-item update
        const payload = {
          itemPN:         item.itemPN,
          description:    item.description,
          issueMult:      Number(item.issueMult),
          uom:            item.uom,
          costEach:       Number(item.costEach),
          convFactor:     Number(item.convFactor),
          vendorPN:       item.vendorPN,
          expires:        item.expires,
          procNotes:      item.procNotes,
          commodityCode:  item.commodityCode,
          defaultCategory:item.defaultCategory,
          costCenters: {
            ccGL:  item.ccGL,
            ccJob: item.ccJob,
            ccWty: item.ccWty,
            ccWo:  item.ccWo,
            ccCapex: item.ccCapex,
            glBm:   item.glBm,
            glEm:   item.glEm,
            glStr:  item.glStr
          },
          indicator: item.indicator
        };
        await axios.put(
          `${process.env.REACT_APP_BASE_URL}common/master-items/${id}`,
          payload,
          { headers }
        );
        alert("Master item updated!");
        navigate("/items-list");
      } else {
        // Storehouse-item update
        await axios.put(
          `${process.env.REACT_APP_BASE_URL}common/items/${id}`,
          item,
          { headers }
        );
        alert("Storehouse item updated!");
        navigate("/items-list");
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || "Error saving");
    }
  };

  if (loading || !item) {
    return <PageLayout><p>Loading…</p></PageLayout>;
  }

  const fieldCols = { xs: 12, md: 3, className: "mb-3" };

  return (
    <PageLayout>
      <Grid container spacing={2} className="align-items-center mb-3">
        <Grid size={{ xs: 12, md: 6 }}>
          <Breadcrumb title={isMaster ? "Edit Master Item" : "Edit Item"} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} className="text-end">
          <Link to="/items-list">
            <MuiButton variant="contained" color="secondary">
              Back to {isMaster ? "Master List" : "Items List"}
            </MuiButton>
          </Link>
        </Grid>
      </Grid>

      <Box className="mc-card">
        <Grid container spacing={2}>
          {isMaster ? (
            // --- Master fields ---
            <>
              {[
                ["Item PN","itemPN"],["Description","description"],
                ["Issue Mult","issueMult"],["UOM","uom"],
                ["Cost Each","costEach"],["Conv","convFactor"],
                ["Vendor PN","vendorPN"],["Expires","expires"],
                ["Proc Notes","procNotes"],
                ["Commodity Code","commodityCode"],
                ["Default Category","defaultCategory"],
                ["CCGL","ccGL"],["CC_JOB","ccJob"],
                ["CC_WTY","ccWty"],["CC_WO","ccWo"],
                ["CC_CAPEX","ccCapex"],["GL_BM","glBm"],
                ["GL_EM","glEm"],["GL_STR","glStr"]
              ].map(([label,field])=>(
                <Grid size={{ xs: 12, md: 3 }} className="mb-3" key={field}>
                  <CardHeader title={label} />
                  <TextField
                    fullWidth
                    value={item[field]||""}
                    onChange={e=>setItem({...item,[field]:e.target.value})}
                  />
                </Grid>
              ))}
              <Grid size={{ xs: 12, md: 3 }} className="mb-3">
                <CardHeader title="Indicator" />
                <TextField
                  select fullWidth
                  value={item.indicator.toString()}
                  onChange={e=>setItem({...item,indicator:e.target.value==="true"})}
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </TextField>
              </Grid>
            </>
          ) : (
            // --- Storehouse fields ---
            <>
              {[
                {label:"Storehouse",field:"storehouse",options:storehouses},
                {label:"Tab",        field:"tab",        options:tabs,
                  display:o=>`${o.name} (${o.index})`},
                {label:"Quadrant",   field:"quadrant",   options:quadrants,
                  display:o=>`${o.name} (${o.index})`},
                {label:"Filter",     field:"filter",     options:filters,
                  display:o=>`${o.name} (${o.index})`}
              ].map(({label,field,options,display})=>(
                <Grid size={{ xs: 12, md: 3 }} className="mb-3" key={field}>
                  <CardHeader title={label} />
                  <TextField
                    select fullWidth
                    value={item[field]}
                    onChange={e=>setItem({...item,[field]:e.target.value})}
                  >
                    {options.map(o=>(
                      <MenuItem key={o._id} value={o._id}>
                        {display ? display(o) : o.code||o.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}

              {[
                ["Item PN","itemPN"],["Item Name","itemName"],
                ["LocID","locId"],["Vendor","vendor"],
                ["EAS PN","easPN"]
              ].map(([label,field])=>(
                <Grid size={{ xs: 12, md: 3 }} className="mb-3" key={field}>
                  <CardHeader title={label} />
                  <TextField
                    fullWidth
                    value={item[field]||""}
                    onChange={e=>setItem({...item,[field]:e.target.value})}
                  />
                </Grid>
              ))}

              <Grid size={{ xs: 12, md: 3 }} className="mb-3">
                <CardHeader title="Always Count" />
                <TextField
                  select fullWidth
                  value={item.alwaysCount.toString()}
                  onChange={e=>setItem({...item,alwaysCount:e.target.value==="true"})}
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} className="mb-3">
                <CardHeader title="Active" />
                <TextField
                  select fullWidth
                  value={item.active.toString()}
                  onChange={e=>setItem({...item,active:e.target.value==="true"})}
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>

              {[
                ["Description","description"],
                ["Bucket","bucket"],
                ["Min","min"],["Max","max"],
                ["Order Mult.","orderMult"],
                ["Group","group"],["Shelf","shelf"]
              ].map(([label,field])=>(
                <Grid size={{ xs: 12, md: 4 }} className="mb-3" key={field}>
                  <CardHeader title={label} />
                  <TextField
                    fullWidth
                    type={["min","max","orderMult"].includes(field)?"number":"text"}
                    value={item[field]||""}
                    onChange={e=>setItem({...item,[field]:e.target.value})}
                  />
                </Grid>
              ))}
            </>
          )}
        </Grid>

        <Grid container spacing={2} className="mt-3">
          <Grid size={12} className="text-end">
            <MuiButton variant="contained" color="primary" onClick={saveItem}>
              {isMaster ? "Save Master Item" : "Save Item"}
            </MuiButton>
          </Grid>
        </Grid>
      </Box>
    </PageLayout>
  );
}
